/**
 * Auth context.
 *
 * Two ways to land in `authenticated`:
 *  - JWT in sessionStorage from a recent tab (cheap path, no biometric).
 *  - Silent WebAuthn assertion (passkey already registered) — does fire the
 *    OS biometric prompt, but only as a one-tap unlock.
 *
 * Logout is no longer a local-only flag: it removes the Device row server-
 * side so the same passkey can't immediately re-authenticate.
 */
import { startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import {
  ApiError,
  auth as authApi,
  me as meApi,
  type MeResponse,
} from './api.js';
import { computeFingerprint, readToken, writeToken } from './storage.js';

export interface AuthState {
  status: 'loading' | 'guest' | 'authenticated';
  me: MeResponse | null;
}

interface AuthContextValue extends AuthState {
  refresh: () => Promise<void>;
  setToken: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  fingerprint: () => Promise<string>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading', me: null });

  const refresh = useCallback(async () => {
    const token = readToken();
    if (!token) {
      setState({ status: 'guest', me: null });
      return;
    }
    try {
      const data = await meApi.get();
      setState({ status: 'authenticated', me: data });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) writeToken(null);
      setState({ status: 'guest', me: null });
    }
  }, []);

  const setToken = useCallback(
    async (token: string) => {
      writeToken(token);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    // Best-effort: tell the server to forget our device. If we're offline or
    // the JWT is already invalid, fall through to the local cleanup so the
    // user always lands on /login.
    try {
      await meApi.deleteDevice();
    } catch {
      /* ignore */
    }
    writeToken(null);
    setState({ status: 'guest', me: null });
  }, []);

  const fingerprint = useCallback(() => computeFingerprint(), []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // 1. Existing JWT? Try the cheap /me round-trip first.
      const token = readToken();
      if (token) {
        try {
          const data = await meApi.get();
          if (!cancelled) setState({ status: 'authenticated', me: data });
          return;
        } catch (err) {
          if (err instanceof ApiError && err.status === 401) writeToken(null);
        }
      }

      // 2. No JWT — try a silent passkey assertion. The OS prompt does pop
      //    up here; the user can dismiss it to land on /login as a guest.
      if (!browserSupportsWebAuthn()) {
        if (!cancelled) setState({ status: 'guest', me: null });
        return;
      }
      try {
        const options = await authApi.webauthnAssertOptions();
        const response = await startAuthentication({ optionsJSON: options });
        const verified = await authApi.webauthnAssertVerify(response);
        writeToken(verified.token);
        const meData = await meApi.get();
        if (!cancelled) setState({ status: 'authenticated', me: meData });
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) writeToken(null);
        if (!cancelled) setState({ status: 'guest', me: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, refresh, setToken, logout, fingerprint }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
